import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { CONNECT_FILTERS, OLDER_THAN_MONTHS_OPTIONS, type ConnectFilter } from "./constants";

interface PayoutReportFiltersProps {
  readonly connect: ConnectFilter;
  readonly olderThanMonths: number | null;
  readonly onConnectChange: (connect: ConnectFilter) => void;
  readonly onOlderThanMonthsChange: (months: number | null) => void;
  readonly onReset: () => void;
}

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";
const LABEL_CLASS = "font-sans text-sm font-medium text-foreground";

export function PayoutReportFilters({
  connect,
  olderThanMonths,
  onConnectChange,
  onOlderThanMonthsChange,
  onReset,
}: PayoutReportFiltersProps) {
  const { t } = useTranslation();
  const hasFilters = connect !== "all" || olderThanMonths !== null;

  return (
    <div data-testid="payout-report-filters" className="flex flex-wrap items-end gap-3">
      <div className="space-y-1.5">
        <label htmlFor="payout-connect" className={LABEL_CLASS}>
          {t("finance.report.filters.connect")}
        </label>
        <select
          id="payout-connect"
          data-testid="filter-connect"
          value={connect}
          onChange={(e) => onConnectChange(e.target.value as ConnectFilter)}
          className={SELECT_CLASS}
        >
          {CONNECT_FILTERS.map((value) => (
            <option key={value} value={value}>
              {t(`finance.report.filters.connectOptions.${value}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="payout-age" className={LABEL_CLASS}>
          {t("finance.report.filters.olderThan")}
        </label>
        <select
          id="payout-age"
          data-testid="filter-age"
          value={olderThanMonths ?? ""}
          onChange={(e) =>
            onOlderThanMonthsChange(e.target.value === "" ? null : Number(e.target.value))
          }
          className={SELECT_CLASS}
        >
          <option value="">{t("finance.report.filters.olderThanAny")}</option>
          {OLDER_THAN_MONTHS_OPTIONS.map((months) => (
            <option key={months} value={months}>
              {t("finance.report.filters.months", { count: months })}
            </option>
          ))}
        </select>
      </div>

      {hasFilters && (
        <Button data-testid="payout-report-filters-reset" variant="outline" onClick={onReset}>
          {t("finance.report.filters.reset")}
        </Button>
      )}
    </div>
  );
}
